<script setup lang="ts">
import { ref, onUnmounted } from 'vue'
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from 'lucide-vue-next'

interface Toast {
  id: number
  message: string
  type: 'success' | 'error' | 'info' | 'warning'
}

const toasts = ref<Toast[]>([])
let counter = 0

function show(message: string, type: Toast['type'] = 'info', duration = 5000) {
  const id = ++counter
  toasts.value.push({ id, message, type })
  setTimeout(() => dismiss(id), duration)
}

function dismiss(id: number) {
  const idx = toasts.value.findIndex((t) => t.id === id)
  if (idx !== -1) toasts.value.splice(idx, 1)
}

const iconMap = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
  warning: AlertTriangle,
}

const styleMap = {
  success: 'border-emerald-500/40 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/80 dark:text-emerald-100',
  error: 'border-rose-500/40 bg-rose-50 text-rose-900 dark:bg-rose-950/80 dark:text-rose-100',
  info: 'border-blue-500/40 bg-blue-50 text-blue-900 dark:bg-blue-950/80 dark:text-blue-100',
  warning: 'border-amber-500/40 bg-amber-50 text-amber-900 dark:bg-amber-950/80 dark:text-amber-100',
}

const iconColorMap = {
  success: 'text-emerald-600 dark:text-emerald-400',
  error: 'text-rose-600 dark:text-rose-400',
  info: 'text-blue-600 dark:text-blue-400',
  warning: 'text-amber-600 dark:text-amber-400',
}

window.__ctiToast = { success: (m: string) => show(m, 'success'), error: (m: string) => show(m, 'error'), info: (m: string) => show(m, 'info'), warning: (m: string) => show(m, 'warning') }

onUnmounted(() => { delete window.__ctiToast })
</script>

<template>
  <Teleport to="body">
    <div class="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
      <TransitionGroup name="toast" tag="div" class="flex flex-col gap-2">
        <div
          v-for="t in toasts"
          :key="t.id"
           :class="['pointer-events-auto flex items-start gap-3 rounded-lg border px-4 py-3 shadow-lg min-w-[280px] max-w-[calc(100vw-2rem)] sm:max-w-[400px]', styleMap[t.type]]"
          role="alert"
          aria-live="polite"
        >
          <component :is="iconMap[t.type]" :class="['size-5 shrink-0 mt-0.5', iconColorMap[t.type]]" />
          <p class="text-sm font-medium flex-1">{{ t.message }}</p>
          <button @click="dismiss(t.id)" class="shrink-0 opacity-60 hover:opacity-100 transition-opacity" aria-label="Cerrar">
            <X class="size-4" />
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-enter-active, .toast-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.toast-enter-from {
  opacity: 0;
  transform: translateX(120%);
}
.toast-leave-to {
  opacity: 0;
  transform: translateX(120%);
}
.toast-move {
  transition: transform 0.25s ease;
}
</style>